import { c as _c } from "react/compiler-runtime";
import { ProgressBar } from '@systemeio/components';
import { CheckCircleIcon, InfoCircleIcon, XCircleIcon } from '@systemeio/icons';
import { NotificationItemView } from 'modules/notifications/components/notification-item-view';
import { SharingFailReasonEnum } from 'modules/share/enum/sharing-fail-reason-enum';
import { SharingStatusEnum } from 'modules/share/enum/sharing-status-enum';
import { useCancelSharing } from 'modules/share/hooks/use-cancel-sharing';
import { useSharingStatusPolling } from 'modules/share/hooks/use-sharing-status-polling';
import type { NotificationSharingItemInterface } from 'modules/share/types/sharing-notification-interface';
import { type ActionMenuItemProps } from 'shared/components';
import { RelativeTime } from 'shared/components/relative-time';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url';
export interface SharingNotificationProps {
  actions?: ActionMenuItemProps[];
  className?: string;
  notification: NotificationSharingItemInterface;
}
const SharingNotificationItem = t0 => {
  const $ = _c(47);
  const {
    actions,
    className,
    notification: notificationItem
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    cancelSharing,
    isCancelling
  } = useCancelSharing();
  const t1 = notificationItem.data.status === SharingStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.sharingId || $[1] !== t1) {
    t2 = {
      sharingId: notificationItem.data.sharingId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.sharingId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    sharingStatus,
    mutate
  } = useSharingStatusPolling(t2);
  let t3;
  if ($[3] !== sharingStatus) {
    t3 = sharingStatus ?? {};
    $[3] = sharingStatus;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== notificationItem.data || $[6] !== t3) {
    t4 = {
      ...notificationItem.data,
      ...t3
    };
    $[5] = notificationItem.data;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== notificationItem || $[9] !== t4) {
    t5 = {
      ...notificationItem,
      data: t4
    };
    $[8] = notificationItem;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const notification = t5;
  const sharedResourceId = notification.data.sharedResourceId;
  let t6;
  if ($[11] !== notification.data.status || $[12] !== sharedResourceId) {
    t6 = notification.data.status === SharingStatusEnum.Success && sharedResourceId ? () => {
      window.open(getWorkflowEditorUrl(sharedResourceId), "_blank");
    } : undefined;
    $[11] = notification.data.status;
    $[12] = sharedResourceId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleNotificationClick = t6;
  let t7;
  if ($[14] !== cancelSharing || $[15] !== mutate || $[16] !== notification.data.sharingId) {
    t7 = async () => {
      await cancelSharing(notification.data.sharingId);
      await mutate(_temp, false);
    };
    $[14] = cancelSharing;
    $[15] = mutate;
    $[16] = notification.data.sharingId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleCancel = t7;
  let t8;
  if ($[18] !== notification.data.failReason || $[19] !== notification.data.status) {
    t8 = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return <InfoCircleIcon className="text-icon-warning size-5 shrink-0" />;
          }
        case SharingStatusEnum.Failed:
          {
            if (notification.data.failReason === SharingFailReasonEnum.CancelledByUser) {
              return <XCircleIcon className="text-icon-disabled size-5 shrink-0" />;
            }
            return <XCircleIcon className="text-icon-error size-5 shrink-0" />;
          }
        case SharingStatusEnum.Success:
          {
            return <CheckCircleIcon className="text-icon-success size-5 shrink-0" />;
          }
        default:
          {
            return <InfoCircleIcon className="text-icon-warning size-5 shrink-0" />;
          }
      }
    };
    $[18] = notification.data.failReason;
    $[19] = notification.data.status;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const getIcon = t8;
  let t9;
  if ($[21] !== notification.data.failReason || $[22] !== notification.data.status || $[23] !== t) {
    t9 = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return t("dashboard.notifications.workflow_sharing_in_progress");
          }
        case SharingStatusEnum.Failed:
          {
            switch (notification.data.failReason) {
              case SharingFailReasonEnum.CancelledByUser:
                {
                  return t("dashboard.notifications.workflow_sharing_cancelled");
                }
              case SharingFailReasonEnum.FailedToShare:
                {
                  return t("dashboard.notifications.workflow_sharing_failed_to_share");
                }
              case SharingFailReasonEnum.ProcessingTimeExceeded:
                {
                  return t("dashboard.notifications.workflow_sharing_processing_time_exceeded");
                }
              case SharingFailReasonEnum.ResourceRemoved:
                {
                  return t("dashboard.notifications.workflow_sharing_resource_removed");
                }
              default:
                {
                  return t("dashboard.notifications.workflow_sharing_failed");
                }
            }
          }
        case SharingStatusEnum.Success:
          {
            return t("dashboard.notifications.workflow_sharing_completed");
          }
      }
    };
    $[21] = notification.data.failReason;
    $[22] = notification.data.status;
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const getMessage = t9;
  let t10;
  if ($[25] !== notification.data.progressPercent || $[26] !== notification.data.status || $[27] !== notification.data.updatedAt || $[28] !== user) {
    t10 = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return notification.data.progressPercent !== undefined && <ProgressBar labelPosition="side" value={notification.data.progressPercent} />;
          }
        case SharingStatusEnum.Failed:
        case SharingStatusEnum.Success:
          {
            return user && <RelativeTime time={notification.data.updatedAt} />;
          }
        default:
          {
            return;
          }
      }
    };
    $[25] = notification.data.progressPercent;
    $[26] = notification.data.status;
    $[27] = notification.data.updatedAt;
    $[28] = user;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const getDescription = t10;
  const t11 = notification.read;
  let t12;
  if ($[30] !== getIcon) {
    t12 = getIcon();
    $[30] = getIcon;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== getMessage) {
    t13 = getMessage();
    $[32] = getMessage;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== getDescription) {
    t14 = getDescription();
    $[34] = getDescription;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = notification.data.status === SharingStatusEnum.Running;
  let t16;
  if ($[36] !== actions || $[37] !== className || $[38] !== handleCancel || $[39] !== handleNotificationClick || $[40] !== isCancelling || $[41] !== notification.read || $[42] !== t12 || $[43] !== t13 || $[44] !== t14 || $[45] !== t15) {
    t16 = <NotificationItemView className={className} isRead={t11} actions={actions} icon={t12} message={t13} description={t14} showCancelButton={t15} isCancelling={isCancelling} onClick={handleNotificationClick} onCancel={handleCancel} />;
    $[36] = actions;
    $[37] = className;
    $[38] = handleCancel;
    $[39] = handleNotificationClick;
    $[40] = isCancelling;
    $[41] = notification.read;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  return t16;
};
export default SharingNotificationItem;
function _temp(data) {
  if (!data) {
    return;
  }
  return {
    ...data,
    status: SharingStatusEnum.Failed,
    failReason: SharingFailReasonEnum.CancelledByUser
  };
}